import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { API_BASE_URL } from '../../config/api';
import BrandWordmark from '../../components/BrandWordmark';
import SignaturePad from '../../components/SignaturePad';

const B = {
  primary: '#2563eb',
  navy: '#0B2D4D',
  yes: '#34C759',
  no: '#FF3B30',
  na: '#FF9500',
  bg: '#f8fafc',
  cardBg: '#fff',
  border: '#e2e8f0',
  textPrimary: '#1e293b',
  textSecondary: '#64748b',
  sectionHeaderBg: '#f0f4f8',
  sectionHeaderBorder: '#2563eb',
  imageUploadBg: '#eff6ff',
  imageUploadBorder: '#2563eb',
};

interface Question {
  question_id: string;
  question_text: string;
  question_type: string;
  required: 0 | 1 | boolean;
  image_required?: number | boolean;
  force_image_on_yes?: number | boolean;
  force_image_on_no?: number | boolean;
  options?: any[];
  allow_multiple_selections?: number | boolean;
  order_number?: number;
}

interface TempAsset {
  whole_good_number?: string | null;
  manufacturer?: string | null;
  model?: string | null;
  specification?: string | null;
  serial_number?: string | null;
  registration_number?: string | null;
  recorded_hours_on_delivery?: number | null;
}

interface ApiCheckPayload {
  check_id: string;
  check_name: string;
  business_name?: string | null;
  driver_name?: string | null;
  driver_email?: string | null;
  expires_at: string;
}

interface ApiData {
  check: ApiCheckPayload;
  questions: Question[];
  temporaryAsset?: TempAsset | null;
  asset?: any;
  waiver: { required: boolean; text: string | null };
}

// ────────────────────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────────────────────
const DriverCheck: React.FC = () => {
  const { token } = useParams<{ token: string }>();

  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [data, setData] = useState<ApiData | null>(null);

  // Response state
  const [responses, setResponses] = useState<Record<string, string>>({});
  const [responseNotes, setResponseNotes] = useState<Record<string, string>>({});
  const [responseImages, setResponseImages] = useState<Record<string, string[]>>({});
  const [uploadingFor, setUploadingFor] = useState<string | null>(null);
  const [signatoryName, setSignatoryName] = useState('');
  const [recordedHours, setRecordedHours] = useState('');

  // Waiver state
  const [waiverSignatoryName, setWaiverSignatoryName] = useState('');
  const [waiverSignatoryRole, setWaiverSignatoryRole] = useState('');
  const [waiverSignatoryCompany, setWaiverSignatoryCompany] = useState('');
  const [waiverIntendedUse, setWaiverIntendedUse] = useState('');
  const [waiverHazards, setWaiverHazards] = useState<Record<string, boolean>>({
    'I am the recipient of this article and have authority to accept it': false,
    'I understand the article must be examined before use and used safely': false,
    'I will provide any further information needed for safe use to my organisation': false,
  });
  const [waiverSignature, setWaiverSignature] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const loadData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setErrorMessage(null);
    try {
      const res = await axios.get(`${API_BASE_URL}/public/delivery/${token}`);
      if (!res.data?.success) {
        throw new Error(res.data?.error || 'Failed to load delivery check');
      }
      const payload: ApiData = {
        check: res.data.check,
        questions: res.data.questions || [],
        temporaryAsset: res.data.temporaryAsset || null,
        asset: res.data.asset || null,
        waiver: res.data.waiver || { required: false, text: null },
      };
      setData(payload);
      if (payload.check?.driver_name) setSignatoryName(payload.check.driver_name);
      if (payload.temporaryAsset?.recorded_hours_on_delivery != null) {
        setRecordedHours(String(payload.temporaryAsset.recorded_hours_on_delivery));
      }
      if (payload.check?.driver_name) setWaiverSignatoryName(payload.check.driver_name);
    } catch (e: any) {
      setErrorMessage(e?.response?.data?.error || e?.message || 'Failed to load delivery check');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleResponseChange = (questionId: string, value: string) => {
    setResponses(prev => ({ ...prev, [questionId]: value }));
  };

  const handleNotesChange = (questionId: string, value: string) => {
    setResponseNotes(prev => ({ ...prev, [questionId]: value }));
  };

  const handleImageUpload = async (questionId: string, file: File) => {
    if (!token) return;
    setUploadingFor(questionId);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('question_id', questionId);
      const res = await axios.post(`${API_BASE_URL}/public/delivery/${token}/upload-image`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.success && res.data.url) {
        setResponseImages(prev => ({
          ...prev,
          [questionId]: [...(prev[questionId] || []), res.data.url],
        }));
      } else {
        throw new Error(res.data?.error || 'Failed to upload image');
      }
    } catch (e: any) {
      alert(e?.response?.data?.error || e?.message || 'Failed to upload image');
    } finally {
      setUploadingFor(null);
    }
  };

  const handleRemoveImage = (questionId: string, idx: number) => {
    setResponseImages(prev => {
      const list = [...(prev[questionId] || [])];
      list.splice(idx, 1);
      return { ...prev, [questionId]: list };
    });
  };

  const validate = (): string | null => {
    if (!data) return null;
    for (const q of data.questions) {
      const req = q.required === 1 || q.required === true;
      if (!req) continue;
      if (q.question_type === 'text_header') continue;
      const v = responses[q.question_id];
      if (!v || !String(v).trim()) {
        return `Please answer: ${q.question_text}`;
      }
    }
    if (data.waiver.required) {
      if (!waiverSignatoryName.trim()) return 'Please enter your name for the Section 6(8) waiver.';
      if (!waiverSignature) return 'Please sign the Section 6(8) waiver.';
      const acks = Object.values(waiverHazards);
      if (acks.some(a => !a)) return 'Please tick all Section 6(8) acknowledgements.';
    }
    if (!signatoryName.trim()) return 'Please enter your name as the recipient.';
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) {
      setErrorMessage(err);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (!token || !data) return;
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const responsePayload = data.questions.map(q => ({
        questionId: q.question_id,
        question_id: q.question_id,
        responseText: responses[q.question_id] || null,
        deliveryNotes: responseNotes[q.question_id] || null,
        images: responseImages[q.question_id] || [],
      }));

      const body: any = {
        responses: responsePayload,
        signatoryName: signatoryName.trim() || null,
        recordedHours: recordedHours ? Number(recordedHours) : null,
      };
      if (data.waiver.required) {
        body.waiver = {
          signatoryName: waiverSignatoryName.trim(),
          signatoryRole: waiverSignatoryRole.trim() || null,
          signatoryCompany: waiverSignatoryCompany.trim() || null,
          intendedUse: waiverIntendedUse.trim() || null,
          hazardsAcknowledged: Object.entries(waiverHazards).map(([label, accepted]) => ({ label, accepted })),
          waiverTextSnapshot: data.waiver.text,
          signatureImage: waiverSignature,
        };
      }

      const res = await axios.post(`${API_BASE_URL}/public/delivery/${token}/complete`, body);
      if (!res.data?.success) {
        throw new Error(res.data?.error || 'Failed to submit delivery check');
      }
      setSubmitSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e: any) {
      setErrorMessage(e?.response?.data?.error || e?.message || 'Failed to submit delivery check');
    } finally {
      setSubmitting(false);
    }
  };

  // ──────────────────────────────────────────────────────────────────────
  // Render helpers
  // ──────────────────────────────────────────────────────────────────────

  const renderQuestion = (q: Question) => {
    const value = responses[q.question_id] || '';
    const opts = Array.isArray(q.options) ? q.options : [];
    const req = q.required === 1 || q.required === true;

    if (q.question_type === 'text_header') {
      return (
        <Box key={q.question_id} sx={{ p: 1.5, bgcolor: B.sectionHeaderBg, borderLeft: `4px solid ${B.sectionHeaderBorder}`, borderRadius: 1, mt: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: B.navy }}>
            {q.question_text}
          </Typography>
        </Box>
      );
    }

    return (
      <Card key={q.question_id} variant="outlined" sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
            {q.question_text} {req && <span style={{ color: B.no }}>*</span>}
          </Typography>

          {(q.question_type === 'yes_no' || q.question_type === 'yes_no_na') && (
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              {['Yes', 'No', ...(q.question_type === 'yes_no_na' ? ['N/A'] : [])].map(opt => (
                <Button
                  key={opt}
                  variant={value === opt ? 'contained' : 'outlined'}
                  color={value === opt ? (opt === 'Yes' ? 'success' : opt === 'No' ? 'error' : 'warning') : 'primary'}
                  onClick={() => handleResponseChange(q.question_id, opt)}
                  sx={{ minWidth: 80, textTransform: 'none' }}
                >
                  {opt}
                </Button>
              ))}
            </Stack>
          )}

          {q.question_type === 'text' && (
            <TextField
              fullWidth
              multiline
              minRows={2}
              value={value}
              onChange={(e) => handleResponseChange(q.question_id, e.target.value)}
              placeholder="Enter your response"
            />
          )}

          {q.question_type === 'number' && (
            <TextField
              fullWidth
              type="number"
              value={value}
              onChange={(e) => handleResponseChange(q.question_id, e.target.value)}
            />
          )}

          {q.question_type === 'date' && (
            <TextField
              fullWidth
              type="date"
              value={value}
              onChange={(e) => handleResponseChange(q.question_id, e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          )}

          {q.question_type === 'multiple_choice' && opts.length > 0 && (
            <Stack spacing={1}>
              {opts.map((o: any) => {
                const label = typeof o === 'string' ? o : (o.option_text || '');
                return (
                  <Button
                    key={label}
                    variant={value === label ? 'contained' : 'outlined'}
                    onClick={() => handleResponseChange(q.question_id, label)}
                    sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                  >
                    {label}
                  </Button>
                );
              })}
            </Stack>
          )}

          {q.question_type === 'signature' && (
            <SignaturePad value={value} onChange={(v) => handleResponseChange(q.question_id, v)} />
          )}

          {q.question_type === 'image_type' && (
            <Typography variant="body2" color="text.secondary">
              Please attach a photo below.
            </Typography>
          )}

          {/* Notes field */}
          <TextField
            fullWidth
            multiline
            minRows={1}
            placeholder="Notes (optional)"
            value={responseNotes[q.question_id] || ''}
            onChange={(e) => handleNotesChange(q.question_id, e.target.value)}
            sx={{ mt: 1.5 }}
            size="small"
          />

          {/* Image upload */}
          <Box sx={{ mt: 1.5 }}>
            <Button
              component="label"
              variant="outlined"
              startIcon={<CameraAltIcon />}
              size="small"
              disabled={uploadingFor === q.question_id}
              sx={{ textTransform: 'none' }}
            >
              {uploadingFor === q.question_id ? 'Uploading…' : 'Add photo'}
              <input
                hidden
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleImageUpload(q.question_id, file);
                  e.target.value = '';
                }}
              />
            </Button>
            {(responseImages[q.question_id] || []).length > 0 && (
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                {(responseImages[q.question_id] || []).map((url, idx) => (
                  <Box key={idx} sx={{ position: 'relative', display: 'inline-block' }}>
                    <Box component="img" src={url} sx={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: B.border }} />
                    <Button
                      size="small"
                      onClick={() => handleRemoveImage(q.question_id, idx)}
                      sx={{ position: 'absolute', top: -8, right: -8, minWidth: 24, p: 0.25, bgcolor: '#fff', borderRadius: '50%' }}
                    >
                      <DeleteIcon fontSize="small" color="error" />
                    </Button>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  // ──────────────────────────────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: B.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (errorMessage && !data) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: B.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Card sx={{ maxWidth: 480 }}>
          <CardContent>
            <Stack spacing={2} alignItems="center" sx={{ py: 2 }}>
              <ErrorOutlineIcon sx={{ fontSize: 48, color: B.no }} />
              <Typography variant="h6">Link unavailable</Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                {errorMessage}
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (submitSuccess) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: B.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Card sx={{ maxWidth: 480 }}>
          <CardContent>
            <Stack spacing={2} alignItems="center" sx={{ py: 2 }}>
              <CheckCircleIcon sx={{ fontSize: 56, color: B.yes }} />
              <Typography variant="h5" sx={{ fontWeight: 700 }}>Thank you!</Typography>
              <Typography variant="body1" color="text.secondary" textAlign="center">
                Your delivery check has been submitted. You can now close this window.
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (!data) return null;

  const tempAsset = data.temporaryAsset;
  const assetSummary = tempAsset
    ? [tempAsset.manufacturer, tempAsset.model, tempAsset.specification].filter(Boolean).join(' · ')
    : data.asset
      ? [data.asset.manufacturer, data.asset.model].filter(Boolean).join(' · ')
      : '';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: B.bg, pb: 8 }}>
      <Box sx={{ bgcolor: B.navy, color: '#fff', py: 3, px: 2 }}>
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <BrandWordmark variant="h5" onDark />
            <Box sx={{ flex: 1 }} />
            {data.check.business_name && (
              <Chip label={data.check.business_name} sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff' }} />
            )}
          </Box>
          <Typography variant="h5" sx={{ mt: 2, fontWeight: 700 }}>
            {data.check.check_name || 'Delivery check'}
          </Typography>
          {data.check.expires_at && (
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              Link expires {new Date(data.check.expires_at).toLocaleString()}
            </Typography>
          )}
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ mt: 3 }}>
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>
        )}

        {/* Asset summary */}
        {(tempAsset || data.asset) && (
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">Item being delivered</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {tempAsset?.whole_good_number || data.asset?.asset_name || 'Delivery item'}
              </Typography>
              {assetSummary && <Typography variant="body2" color="text.secondary">{assetSummary}</Typography>}
              <Stack direction="row" spacing={2} sx={{ mt: 1 }} flexWrap="wrap">
                {tempAsset?.serial_number && <Chip size="small" label={`Serial: ${tempAsset.serial_number}`} />}
                {tempAsset?.registration_number && <Chip size="small" label={`Reg: ${tempAsset.registration_number}`} />}
                {data.asset?.serial_number && <Chip size="small" label={`Serial: ${data.asset.serial_number}`} />}
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Questions */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Check items
            </Typography>
            {data.questions.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No items to check.</Typography>
            ) : (
              data.questions.map(renderQuestion)
            )}
          </CardContent>
        </Card>

        {/* Waiver section */}
        {data.waiver.required && (
          <Card variant="outlined" sx={{ mt: 2, borderColor: B.na }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <WarningAmberIcon sx={{ color: B.na }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Section 6(8) Waiver
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                By signing below, you confirm receipt of this article and accept responsibility under §6(8) of the Health and Safety at Work etc. Act 1974.
              </Typography>
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: '#fff8f0',
                  border: '1px dashed',
                  borderColor: B.na,
                  borderRadius: 1,
                  fontSize: '0.85rem',
                  whiteSpace: 'pre-wrap',
                  maxHeight: 200,
                  overflowY: 'auto',
                  mb: 2,
                }}
              >
                {data.waiver.text}
              </Box>

              <Stack spacing={2}>
                <TextField
                  fullWidth
                  required
                  label="Signatory name"
                  value={waiverSignatoryName}
                  onChange={(e) => setWaiverSignatoryName(e.target.value)}
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    fullWidth
                    label="Role / job title"
                    value={waiverSignatoryRole}
                    onChange={(e) => setWaiverSignatoryRole(e.target.value)}
                  />
                  <TextField
                    fullWidth
                    label="Company"
                    value={waiverSignatoryCompany}
                    onChange={(e) => setWaiverSignatoryCompany(e.target.value)}
                  />
                </Stack>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Intended use (optional)"
                  value={waiverIntendedUse}
                  onChange={(e) => setWaiverIntendedUse(e.target.value)}
                />

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Please acknowledge
                  </Typography>
                  <Stack>
                    {Object.keys(waiverHazards).map(label => (
                      <FormControlLabel
                        key={label}
                        control={
                          <Checkbox
                            checked={!!waiverHazards[label]}
                            onChange={(e) => setWaiverHazards(prev => ({ ...prev, [label]: e.target.checked }))}
                          />
                        }
                        label={label}
                      />
                    ))}
                  </Stack>
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Signature
                  </Typography>
                  <SignaturePad value={waiverSignature} onChange={setWaiverSignature} height={180} />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Final details */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
              Recipient details
            </Typography>
            <Stack spacing={2}>
              <TextField
                fullWidth
                required
                label="Your name"
                value={signatoryName}
                onChange={(e) => setSignatoryName(e.target.value)}
                helperText="The person taking delivery"
              />
              <TextField
                fullWidth
                type="number"
                label="Recorded hours on delivery (optional)"
                value={recordedHours}
                onChange={(e) => setRecordedHours(e.target.value)}
              />
            </Stack>
          </CardContent>
        </Card>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : undefined}
            sx={{ textTransform: 'none', minWidth: 200, fontWeight: 700 }}
          >
            {submitting ? 'Submitting…' : 'Submit delivery check'}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default DriverCheck;
